import { useEffect, useState, useRef } from "react";
import { Upload, Trash2, Eye, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { getToken } from "../../lib/auth";

export default function Media() {
  const [items, setItems] = useState([]);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/media").then((r) => setItems(r.data.media));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true); setProgress(0);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${getToken()}` },
          onUploadProgress: (p) => setProgress(Math.round((p.loaded * 100) / (p.total || 1))),
        });
      }
      toast.success("Uploaded"); load();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); setProgress(0); e.target.value = ""; }
  };

  const del = async (m) => {
    if (m.usage > 0 && !window.confirm(`This file is used by ${m.usage} product(s). Delete anyway?`)) return;
    if (!m.usage && !window.confirm("Delete this file?")) return;
    await api.delete(`/admin/media/${m.filename}`);
    load();
  };

  const fullUrl = (u) => `${process.env.REACT_APP_BACKEND_URL}${u}`;

  return (
    <div data-testid="admin-media-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#00FF66]">// media</div>
          <h1 className="font-heading font-bold text-3xl mt-1">Media library</h1>
        </div>
        <button onClick={() => fileRef.current?.click()} className="btn-primary" data-testid="media-upload-btn"><Upload className="w-4 h-4" />upload</button>
        <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={upload} />
      </div>

      {uploading && (
        <div className="mb-6 surface p-3">
          <div className="font-mono text-xs text-[#A1A1AA] mb-2">uploading… {progress}%</div>
          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-[#00FF66]" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {items.map((m) => (
          <div key={m.id} className="surface overflow-hidden group" data-testid={`media-item-${m.filename}`}>
            <div className="aspect-square bg-[#0a0a0f] relative">
              <img src={fullUrl(m.url)} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button onClick={() => setPreview(m)} className="btn-ghost !py-1.5 !px-2"><Eye className="w-3.5 h-3.5" /></button>
                <button onClick={() => { navigator.clipboard.writeText(fullUrl(m.url)); toast.success("URL copied"); }} className="btn-ghost !py-1.5 !px-2"><Copy className="w-3.5 h-3.5" /></button>
                <button onClick={() => del(m)} className="btn-ghost !py-1.5 !px-2 hover:!border-[#FF3B30] hover:!text-[#FF3B30]"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              {m.usage > 0 && <div className="absolute top-1 right-1 chip-mono !text-[10px]">used ×{m.usage}</div>}
            </div>
            <div className="p-2 font-mono text-[10px] text-[#A1A1AA] truncate">{m.original_name || m.filename}</div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-[#A1A1AA] font-mono text-sm">no media</div>}
      </div>

      {preview && (
        <div onClick={() => setPreview(null)} className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6">
          <img src={fullUrl(preview.url)} alt="" className="max-h-[90vh] max-w-[90vw] surface" />
        </div>
      )}
    </div>
  );
}
